import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const schema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  subject: z.string().trim().min(1).max(200),
  message: z.string().trim().min(1).max(2000),
  captchaToken: z.string().trim().min(1).max(4096),
});

// Turnstile sitekey is public; served at runtime so the same build works on any
// host. Falls back to VITE_TURNSTILE_SITE_KEY (Lovable's prior name) then the
// original widget, keeping Lovable previews/deploys working with no config.
const DEFAULT_TURNSTILE_SITE_KEY = "0x4AAAAAADr5r_R2Xc8JE4hO";

export const getTurnstileSiteKey = createServerFn({ method: "GET" }).handler(
  async () => ({
    siteKey:
      process.env.TURNSTILE_SITE_KEY ||
      process.env.VITE_TURNSTILE_SITE_KEY ||
      DEFAULT_TURNSTILE_SITE_KEY,
  }),
);

export const sendContact = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => schema.parse(data))
  .handler(async ({ data }) => {
    const ONESIGNAL_APP_ID = process.env.ONESIGNAL_APP_ID;
    const ONESIGNAL_REST_API_KEY = process.env.ONESIGNAL_REST_API_KEY;
    const TO_EMAIL = process.env.CONTACT_TO_EMAIL;
    // Bare sender address; if unset, OneSignal uses the dashboard's verified sender.
    const FROM_EMAIL = process.env.CONTACT_FROM_EMAIL;
    const TURNSTILE_SECRET_KEY = process.env.TURNSTILE_SECRET_KEY;

    if (!ONESIGNAL_APP_ID || !ONESIGNAL_REST_API_KEY || !TO_EMAIL) {
      throw new Error("Email service is not configured.");
    }
    if (!TURNSTILE_SECRET_KEY) {
      throw new Error("Captcha is not configured.");
    }

    // Verify Cloudflare Turnstile token
    const { getRequestIP } = await import("@tanstack/react-start/server");
    const remoteip = getRequestIP({ xForwardedFor: true }) ?? undefined;
    const verifyBody = new URLSearchParams();
    verifyBody.set("secret", TURNSTILE_SECRET_KEY);
    verifyBody.set("response", data.captchaToken);
    if (remoteip) verifyBody.set("remoteip", remoteip);

    const verifyRes = await fetch(
      "https://challenges.cloudflare.com/turnstile/v0/siteverify",
      { method: "POST", body: verifyBody },
    );
    const verifyJson = (await verifyRes.json()) as { success: boolean; "error-codes"?: string[] };
    if (!verifyJson.success) {
      console.error("Turnstile verification failed:", verifyJson["error-codes"]);
      throw new Error("Captcha verification failed");
    }

    const escape = (s: string) =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const html = `
      <h2>New contact message</h2>
      <p><strong>From:</strong> ${escape(data.name)} &lt;${escape(data.email)}&gt;</p>
      <p><strong>Subject:</strong> ${escape(data.subject)}</p>
      <hr/>
      <p>${escape(data.message).replace(/\n/g, "<br/>")}</p>
    `;

    const res = await fetch("https://onesignal.com/api/v1/notifications", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Key ${ONESIGNAL_REST_API_KEY}`,
      },
      body: JSON.stringify({
        app_id: ONESIGNAL_APP_ID,
        include_email_tokens: [TO_EMAIL],
        include_unsubscribed: true, // reach the team inbox even though it never SDK-subscribed
        disable_email_click_tracking: true, // no link-wrapping on an internal alert
        email_subject: `[Contact] ${data.subject}`,
        email_body: html,
        email_reply_to_address: data.email, // hitting reply answers the submitter
        ...(FROM_EMAIL ? { email_from_address: FROM_EMAIL } : {}),
      }),
    });

    // OneSignal answers 200 with an `errors` array when nothing was queued.
    const body = (await res.json().catch(() => null)) as
      | { id?: string; errors?: unknown }
      | null;
    if (!res.ok || !body?.id || body.errors) {
      console.error("OneSignal error:", res.status, JSON.stringify(body));
      throw new Error("Failed to send email");
    }

    return { ok: true };
  });